<script setup lang="ts">
    import { computed } from 'vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { ROW_STATUS_OPTIONS, type GuestRow, type ParticipantStatusName } from './guest-list-types';

    const props = defineProps<{
        row: GuestRow;
        requireAttestation: boolean;
    }>();

    const emit = defineEmits<{
        updateStatus: [status: ParticipantStatusName];
        showResponse: [];
        showProfile: [];
        resendConfirmation: [];
        resendAttestation: [];
    }>();

    const participant = computed(() => props.row.participant);
    const data = computed(() => props.row.data);

    const statusOptions = computed(() =>
        ROW_STATUS_OPTIONS.filter((o) => (!o.attestationOnly || props.requireAttestation) && o.status !== participant.value.StatusName)
    );

    const nameFallback = computed(() => (!participant.value.FirstName || !participant.value.LastName ? data.value.Name : null));
    const hasMenu = computed(() => !!participant.value.ContactId || !!participant.value.EventParticipantData);
</script>

<template>
    <td>
        <BSDropdownButton class="d-inline-block" btnClass="btn-sm text-normal round text-md" closeOnClickInside>
            <template #toggle>
                <i class="fa fa-circle status-circle m-xs-r" :class="'status-' + participant.StatusName.toLowerCase()" aria-hidden="true"></i>
                {{ $Resources['EventGuestList' + participant.StatusName + 'Label'] }}
            </template>
            <template #content>
                <li class="dropdown-header">{{ $Resources.GenericStatus }}:</li>
                <li v-for="option in statusOptions" :key="option.status">
                    <a href="javascript:void(0)" @click="emit('updateStatus', option.status)">
                        {{ $Resources['EventGuestList' + option.status + 'Label'] }}
                    </a>
                </li>
            </template>
        </BSDropdownButton>
    </td>
    <td>
        <div class="d-flex flex-wrap col-gap-sm">
            <span class="fw-600 flex-basis-100">
                {{ participant.FirstName }} {{ participant.LastName }}
                <template v-if="nameFallback">{{ nameFallback }}</template>
                <small v-if="participant.ContactId && participant.EventParticipantData" class="c-light">{{ $Resources.EventGuestListPrimaryGuestLabel }}</small>
                <small v-if="!participant.ContactId" class="c-light">{{ $Resources.EventGuestListAccompanyingLabel }}</small>
            </span>
            <span v-if="participant.Email || data.Email">{{ participant.Email || data.Email }}</span>
            <span v-if="participant.SmsNumber || data.SmsNumber">{{ participant.SmsNumber || data.SmsNumber }}</span>
        </div>
    </td>
    <td>{{ participant.CompanyName || data.CompanyName }}</td>
    <td>
        <time :datetime="participant.Created">{{ formatDateTime(participant.Created) }}</time>
        <br />
        <span v-if="participant.Modified !== participant.Created" class="small">
            {{ $Resources.EventGuestlistModifiedGuest }}:
            <time :datetime="participant.Modified">{{ formatDateTime(participant.Modified) }}</time>
        </span>
    </td>
    <td class="autofit">
        <BSDropdownButton v-if="hasMenu" class="d-inline-block" btnClass="btn-subtle" menuClass="dropdown-menu-right" closeOnClickInside>
            <template #toggle>
                <span class="glyphicon glyphicon-option-vertical"></span>
                <span class="sr-only">{{ $Resources.MoreMenuItem }}</span>
            </template>
            <template #content>
                <li class="dropdown-header">{{ $Resources.MoreMenuItem }}:</li>
                <li v-if="participant.SurveyResponseId">
                    <a href="javascript:void(0)" @click="emit('showResponse')">{{ $Resources.EventGuestlistShowResponse }}</a>
                </li>
                <template v-if="participant.ContactId">
                    <li>
                        <a href="javascript:void(0)" @click="emit('showProfile')">{{ $Resources.EventGuestlistShowProfile }}</a>
                    </li>
                    <li v-if="participant.StatusName === 'Accepted'">
                        <a href="javascript:void(0)" @click="emit('resendConfirmation')">{{ $Resources.EventGuestlistResendConfirmation }}</a>
                    </li>
                    <li v-if="participant.StatusName === 'AttestationPending'">
                        <a href="javascript:void(0)" @click="emit('resendAttestation')">
                            {{ $Resources.EventGuestListResendAttestation }}
                            <template v-if="participant.AttestationEventParticipant?.ReminderSent">
                                <br />
                                <small>{{ $Resources.LastSentLabel }}:</small>
                                {{ formatDateTime(participant.AttestationEventParticipant.ReminderSent) }}
                            </template>
                        </a>
                    </li>
                </template>
            </template>
        </BSDropdownButton>
    </td>
</template>
